/**
 * src/components/ui/Cifra.jsx
 * Una cifra que no se puede pintar sin decir cuándo se midió.
 *
 * Por qué existe: el §3 de docs/ERRORES_RECURRENTES.md es el error más repetido
 * del proyecto —seis casos, tres el mismo día en tres hilos distintos— y siempre
 * es el mismo gesto: `valor ?? 0`. Un dato que falta se convierte en un cero, y
 * el cero se lee como un hecho. Uno de esos ceros lo leía quien estaba decidiendo
 * si contratar.
 *
 * La causa no es descuido. Es que pintar un número no obligaba a distinguir
 * «no hay» de «no he mirado». Este componente lo obliga: `lectura` es un prop
 * REQUERIDO y hay que decidir su valor.
 *
 *   lectura={fecha}   → hay lectura de esa fecha
 *   lectura={null}    → no hay lectura, y lo estoy diciendo a propósito
 *   (sin lectura)     → estado de error visible, y `tests/test_cifra_lectura.py`
 *                       lo caza antes de que llegue a producción
 *
 * Ojo con el alcance: esto es para cifras que salen de una LECTURA del mercado
 * y por tanto pueden estar rancias o no existir. Un recuento vivo de la base
 * («cuántos clientes tengo») no necesita esto y no debe llevarlo: ceremonia sin
 * información es ruido.
 */

/** Formatea la fecha de lectura en el idioma del panel. */
import { diaISOChile, diaISOChileDe, fechaCL, horaCL } from '../../lib/horaChile'

function _fecha(valor) {
  // Hora de Chile SIEMPRE (Manuel, 29-08): la marca viaja en UTC de la casa
  // y el «hoy» es el día chileno, no el del navegador.
  const mismoDia = diaISOChileDe(valor) === diaISOChile()
  if (mismoDia) {
    // 24 h a propósito: el resto del panel escribe las horas así, y el «a. m.»
    // de es-CL termina en punto — pegado a una frase deja dos seguidos.
    return `hoy ${horaCL(valor)}`
  }
  return fechaCL(valor, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Decide qué se está mirando. Separado del render para poder probarlo y para
 * que otras pantallas puedan preguntar sin pintar.
 *
 * Devuelve: 'sin-declarar' | 'sin-lectura' | 'cero' | 'medido'
 */
export function estadoDeLectura(valor, lectura) {
  if (lectura === undefined) return 'sin-declarar'
  if (lectura === null) return 'sin-lectura'
  if (valor === null || valor === undefined) return 'sin-lectura'
  if (valor === 0) return 'cero'
  return 'medido'
}

const _ESTILO_VALOR = {
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  letterSpacing: '-0.02em',
}

const _ESTILO_PIE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-xs)',
  marginTop: 'var(--space-1)',
}

/**
 * @param {number|null} valor      la cifra
 * @param {Date|string|null} lectura  cuándo se midió. `null` = no se ha medido.
 *                                    REQUERIDO: omitirlo es un error visible.
 * @param {string} [nada]         qué es lo que no hay, para el caso cero.
 *                                Ej. nada="ninguna oferta activa"
 * @param {string} [sufijo]       unidad tras la cifra
 * @param {string} [tamano]       token de tamaño; por defecto --text-2xl
 * @param {boolean} [pie=true]    pintar la línea de contexto bajo la cifra
 */
export function Cifra({ valor, lectura, nada, sufijo, tamano = 'var(--text-2xl)', pie = true }) {
  const estado = estadoDeLectura(valor, lectura)

  if (estado === 'sin-declarar') {
    // Ruidoso a propósito: un fallo mudo aquí es exactamente lo que este
    // componente existe para impedir.
    return (
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        color: 'var(--color-error)', background: 'var(--color-error-soft)',
        border: '1px solid var(--color-error-line)',
        borderRadius: 'var(--radius-sm)', padding: 'var(--space-05) var(--space-2)',
      }}>
        ⚠ Cifra sin declarar su lectura
      </span>
    )
  }

  const texto = {
    'sin-lectura': '—',
    cero: '0',
    medido: typeof valor === 'number' ? valor.toLocaleString('es-CL') : String(valor),
  }[estado]

  const color = estado === 'sin-lectura' ? 'var(--color-text-3)' : 'var(--color-text)'

  const cuando = estado === 'sin-lectura' ? null : _fecha(lectura)
  const contexto = {
    'sin-lectura': 'aún sin lectura del mercado',
    cero: cuando ? `${nada || 'ninguno'} en la lectura del ${cuando}` : (nada || 'ninguno'),
    medido: cuando ? `leído ${cuando}` : null,
  }[estado]

  const colorPie = {
    'sin-lectura': 'var(--color-warn)',
    cero: 'var(--color-text-2)',
    medido: 'var(--color-text-3)',
  }[estado]

  return (
    <span>
      <span style={{ ..._ESTILO_VALOR, fontSize: tamano, color }}>
        {texto}{sufijo && estado !== 'sin-lectura' ? ` ${sufijo}` : ''}
      </span>
      {pie && contexto && (
        <span style={{ ..._ESTILO_PIE, color: colorPie, display: 'block' }}>
          {contexto}
        </span>
      )}
    </span>
  )
}
